/**
 * Manufacturer → Revision lines → "Also in other makers' revision lines".
 *
 * Lines another manufacturer owns that hold calibres this one makes or
 * originally made (shared-types 1.143.0 `originalManufacturerId`). ETA's page
 * lists Unitas's 6498 line because ETA makes 6498-1. Read-only: a line is
 * edited on its owner's page, which "Open" goes to.
 */
import { useEffect, useState } from 'react';
import { Button } from '../common/Button';
import { RevisionChipBadge } from '../CuratedWatches/RevisionChipBadge';
import { getOtherMakersLinesForManufacturer, type OtherMakerLine } from '../../services/calibreLineage';
import { currentMember, revisionChip, sortMembers } from '../../services/calibreLineageLogic';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';

interface Props {
  manufacturerId: string;
  /** Open another manufacturer's page. Absent = no Open button. */
  onOpenManufacturer?: (manufacturerId: string) => void;
}

export function OtherMakersRevisionLines({ manufacturerId, onOpenManufacturer }: Props) {
  const [lines, setLines] = useState<OtherMakerLine[] | null>(null);
  const { manufacturers } = useManufacturerCatalogue();
  const nameOf = (id: string) => {
    const m = manufacturers.find((x) => x.id === id);
    return m ? m.displayName || m.name : id;
  };

  useEffect(() => {
    let cancelled = false;
    setLines(null);
    getOtherMakersLinesForManufacturer(manufacturerId)
      .then((ls) => !cancelled && setLines(ls))
      .catch((err) => {
        console.error("Failed to load other makers' revision lines", err);
        if (!cancelled) setLines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [manufacturerId]);

  if (!lines || lines.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="other-makers-lines">
      <div>
        <h4 className="text-sm font-medium text-gray-900">Also in other makers&rsquo; revision lines</h4>
        <p className="text-xs text-gray-500">
          Lines another manufacturer owns that include calibres this one makes or originally made. Edit them on that
          manufacturer&rsquo;s page.
        </p>
      </div>
      <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
        {lines.map(({ lineage, members, ourIds }) => {
          const ms = sortMembers(members);
          const current = currentMember(ms);
          const ours = new Set(ourIds);
          return (
            <li key={lineage.id} className="p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{lineage.baseLabel}</span>{' '}
                  <span className="text-xs text-gray-500">· {nameOf(lineage.manufacturerId)} line</span>
                </p>
                {onOpenManufacturer && (
                  <Button size="sm" variant="ghost" onClick={() => onOpenManufacturer(lineage.manufacturerId)}>
                    Open {nameOf(lineage.manufacturerId)}
                  </Button>
                )}
              </div>
              <ul className="text-sm">
                {ms.map((m) => (
                  <li key={m.id} className="flex items-center gap-2">
                    <span className={ours.has(m.id) ? 'font-medium text-gray-900' : 'text-gray-600'}>{m.name}</span>
                    {m.revisionLabel && <span className="font-mono text-xs text-gray-500">{m.revisionLabel}</span>}
                    <RevisionChipBadge chip={revisionChip(m, current, ms)} />
                    {ours.has(m.id) && (
                      <span className="rounded bg-brand-50 px-1 text-[10px] text-brand-800 border border-brand-200">this maker</span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
